// The RMA number in an RMARTN Cust Pays reply (2026-10-27,
// docs/contact-view.md "The RMA number in the reply"; @cxhub/shared's
// rmartn-cust-pays.js).
//
// The reply keeps RMA_REFERENCE_TOKEN where its Reference ID goes, shown in
// the box as a chip. It is filled from the return step's result:
//   - Process on the return step: as soon as the result has the RMA's
//     erpOrderId;
//   - Process all: after the steps ran, before the send;
//   - plain Send, once the return step ran: from its result.
// When the result has no erpOrderId yet, the server reads it again for up to
// 5 s (fetchRmaReference); then the order's number, with the note recorded.
// Plain Send while the return has not run is blocked
// (RMA_REFERENCE_NOT_PROCESSED); a reply-content block, so deleting the line
// lifts it.
import { rmaReferenceState, fillRmaReference, hasRmaReferenceToken, withoutRmaReferenceToken, RMA_REFERENCE_NOT_PROCESSED, RMA_REFERENCE_FALLBACK_NOTE } from '@cxhub/shared'

export { RMA_REFERENCE_NOT_PROCESSED, withoutRmaReferenceToken, hasRmaReferenceToken }

// Send's warning while the token is in the reply and the return step has not
// run; null otherwise (no token, or Send fills it from the step's result).
export function rmaReferenceSendWarning(reply, actions = [], stepStates = {}) {
  const st = rmaReferenceState(reply, actions, stepStates)
  return st.hasToken && !st.processed ? RMA_REFERENCE_NOT_PROCESSED : null
}

// Process all's warning: the token stays unfilled only when the return step
// has not run and is not in this pass's plan (an unchecked step).
export function rmaReferenceProcessAllWarning(reply, actions = [], stepStates = {}, plan = null) {
  const st = rmaReferenceState(reply, actions, stepStates)
  if (!st.hasToken || st.processed) return null
  if (st.stepIndex >= 0 && (plan?.indexes ?? []).includes(st.stepIndex)) return null
  return RMA_REFERENCE_NOT_PROCESSED
}

// The value for the token from the return step's result: its erpRmaId, else
// what the server's wait gives (fetchRef: () => Promise<{ value, source,
// note }>), else the order's number with the fallback note.
// { value, source, note }.
export async function rmaReferenceFromResult({ result = null, step = null, fetchRef = null } = {}) {
  const direct = result?.erpRmaId ? String(result.erpRmaId) : null
  if (direct) return { value: direct, source: 'rma', note: null }
  if (typeof fetchRef === 'function') {
    try {
      const out = await fetchRef()
      if (out?.value) return { value: String(out.value), source: out.source ?? null, note: out.note ?? null }
    } catch { /* falls back below */ }
  }
  const order = step?.params?.erpOrderId ?? null
  return order ? { value: String(order), source: 'order', note: RMA_REFERENCE_FALLBACK_NOTE } : { value: null, source: null, note: null }
}

// The reply with its token filled, or unchanged when there is no value.
export function replyWithRmaReference(reply, value) {
  return value ? fillRmaReference(reply, value) : String(reply ?? '')
}
